import { css } from '@emotion/react';
import { Payout } from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { PayoutStatus } from '@frontend/api-payouts';
import { Chips } from '@frontend/chips';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { TableColumnConfig } from '@frontend/design-system';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { pluralize, GetLocationName } from '../../../utils/helpers';
import { PayoutStatusChip } from '../PayoutStatusChip';

type GeneratePayoutColumnOptions = {
  isMulti: boolean;
  showPayoutDescriptor: boolean;
};

const locationsHoverMessage = (getLocationName: GetLocationName, locationIds: string[]) => {
  const totalLocations = locationIds.length;
  const accessibleLocations = locationIds.map((id) => getLocationName(id)).filter((val) => !!val);
  const inaccessibleLocations = totalLocations - accessibleLocations.length;
  const suffix =
    inaccessibleLocations > 0 ? ` and ${inaccessibleLocations} other location${pluralize(inaccessibleLocations)}` : '';
  return accessibleLocations.join(', ') + suffix;
};

export const generateColumns =
  ({ showPayoutDescriptor, isMulti }: GeneratePayoutColumnOptions) =>
  (getLocationName: GetLocationName, showLocationName: boolean, forPrint = false): TableColumnConfig<Payout>[] =>
    [
      {
        id: 'location',
        Header: 'Locations',
        accessor: (payout) => payout?.locationId,
        cellRenderer: (locationId: string) => {
          const locationString = getLocationName(locationId ?? '');
          return forPrint ? (
            locationsHoverMessage(getLocationName, [locationId])
          ) : (
            <Chips.LocationChip
              css={css`
                max-width: none;
              `}
            >
              {locationString}
            </Chips.LocationChip>
          );
        },
        cellConfig: {
          showHoverLabel: true,
          hoverLabelMessage: (locationId: string) => locationsHoverMessage(getLocationName, [locationId]),
        },
        disableSortBy: true,
        omit: !showLocationName,
      },
      {
        id: 'amount',
        Header: i18next.t('Amount', { ns: 'payments' }) as string,
        accessor: (payout) => formatCentsToCurrency(payout.amount),
        width: 145,
        disableSortBy: true,
      },
      {
        id: 'status',
        Header: i18next.t('Status', { ns: 'payments' }) as string,
        accessor: (payout) => payout.status?.toUpperCase() || '',
        cellRenderer: (cellValue: PayoutStatus) => (
          <PayoutStatusChip status={cellValue} css={{ width: 'min-content' }} />
        ),
        width: 120,
        disableSortBy: true,
      },
      {
        id: 'initiated',
        Header: i18next.t('Initiated', { ns: 'payments' }) as string,
        // we must use this field and not payout.createdAt
        // payout.createdAt represents the time the payout was created in the weave system, while...
        // payout.payoutCreatedAt represents the time the payout was created in the respective payments platform
        accessor: (payout) => formatDate(payout.payoutCreatedAt, 'MMM D, YYYY'),
        width: 150,
        disableSortBy: true,
      },
      {
        id: 'arrival',
        Header: i18next.t('Est. Arrival', { ns: 'payments' }) as string,
        accessor: (payout) => formatDate(payout.estimatedArrivalTime, 'MMM D, YYYY', true),
        width: 150,
        disableSortBy: true,
      },
      {
        id: 'details',
        Header: i18next.t('Details', { ns: 'payments' }) as string,
        accessor: (payout) => payout.payoutId,
        minWidth: 300,
        disableSortBy: true,
      },
      {
        id: 'payoutDescriptor',
        Header: 'Payout Descriptor',
        accessor: (payout) => payout.payoutDescriptor ?? '',
        disableSortBy: !isMulti,
        omit: !showPayoutDescriptor,
      },
    ];
